/**
 * Campaigns request/response schemas.
 *
 * Single source of truth for the wire format between apps/web (TanStack
 * Query hooks) and apps/api (Hono routes). Both sides import the same
 * Zod schemas; client-side validation can never drift from server-side.
 *
 * Layered structure:
 *   1. Subschemas (channel, sender_id, body_template, toggles, recipient)
 *   2. Composite request shapes (create, update, action, preview, test-send)
 *   3. Response DTOs (campaign list, campaign detail, recipient list, dispatch monitor)
 *
 * Cross-field refines enforced as superRefine() guards on the composite
 * shapes — see the R1..R7 markers below for the threat-model link.
 *
 * NOT validated here (handled elsewhere):
 *   - Authorization (caller user_id matches campaign user_id) — middleware
 *   - Rate limits — middleware (lib/rate-limit.ts)
 *   - Per-recipient HMAC token — derived server-side at dispatch time
 *     (lib/campaigns.ts computeCampaignDlrToken), never accepted from client
 */

import { z } from "zod";
import { flexiblePhoneSchema, phoneSchema, uuidSchema } from "./primitives";

// =================================================================
// 1. Subschemas — atomic building blocks
// =================================================================

/**
 * Channel: SMS or Viber. Telegram is in roadmap but not enabled in
 * S4.B-NEW; including it here would force route handlers to add a
 * runtime guard right after Zod validation. We'd rather widen the
 * enum at the same time the dispatch path adds telegram support.
 */
export const campaignChannelSchema = z.enum(["sms", "viber", "whatsapp"]);
export type CampaignChannel = z.infer<typeof campaignChannelSchema>;

/**
 * Sender id (the "from" field on sendSMS API). Per upstream docs:
 * max 11 alphanumeric chars; in practice the upstream also accepts
 * digits-only short codes ("1898") and dot ("Info.SMS"). We mirror
 * that with /^[A-Za-z0-9 .]{1,11}$/.
 *
 * R5: keep this pattern in sync with apps/api/src/routes/messages.ts
 * if the latter ever validates sender_id explicitly. Today the upstream
 * is the only enforcer; we add a thin client-side guard for fast UX.
 *
 * Note: leading/trailing space is stripped via .trim() but inner
 * spaces are allowed (e.g. "My Brand").
 */
export const campaignSenderIdSchema = z
  .string()
  .trim()
  .min(1, "sender_id_empty")
  .max(11, "sender_id_too_long")
  .regex(/^[A-Za-z0-9 .]{1,11}$/, "sender_id_invalid_chars");
export type CampaignSenderId = z.infer<typeof campaignSenderIdSchema>;

/**
 * Campaign name — human-readable label shown on dashboards and audit
 * logs. Plain text, no markdown / HTML allowed.
 *
 * Length cap of 120 chars matches messages_templates.name in DB schema
 * 0023 so users can promote a template to a campaign and reuse the name
 * without truncation.
 */
export const campaignNameSchema = z
  .string()
  .trim()
  .min(1, "name_empty")
  .max(120, "name_too_long");
export type CampaignName = z.infer<typeof campaignNameSchema>;

/**
 * Body template — the SMS text with optional `{{var_name}}` placeholders.
 *
 * Upper bound 1530 chars = 9 GSM-7 concatenated parts (153 chars each
 * after UDH overhead) which is the practical sweet spot for SMS in
 * Romania per market practice. Anything longer should be a campaign
 * splitting decision, not a single-message decision.
 *
 * Lower bound 1 — cannot send an empty SMS. (Even a single space is
 * valid; we leave that as a UX problem for the frontend to flag.)
 *
 * R1 (force_gsm7) and R2 (gdpr placeholder) are enforced at the
 * composite-create level via superRefine, not here, because they
 * depend on sibling fields.
 */
export const campaignBodyTemplateSchema = z
  .string()
  .min(1, "body_empty")
  .max(1530, "body_too_long");
export type CampaignBodyTemplate = z.infer<typeof campaignBodyTemplateSchema>;

/**
 * Behaviour toggles (PIVOT v3 design). Defaults match the sendSMS.ro
 * reference UI so a user creating a campaign here gets the same
 * end-to-end behaviour they'd get on the upstream platform unless they
 * explicitly opt out.
 *
 *   - dedupe_recipients=false: by default keep duplicates; the upstream
 *     filters them (10-per-2h native dedup) so user controls visibility.
 *   - apply_user_blocklist=true: pre-filter against user's local blocklist
 *     cache (S5) before queuing. Saves credits + reduces noise.
 *   - add_unsubscribe_link=false: emit `message_send` (no GDPR link)
 *     unless explicitly enabled. Required for marketing in EU.
 *   - force_gsm7=true: refuse to send if body contains UCS-2 chars,
 *     to keep cost predictable. R1 enforces this server-side.
 */
export const campaignTogglesSchema = z
  .object({
    dedupe_recipients: z.boolean().default(false),
    apply_user_blocklist: z.boolean().default(true),
    add_unsubscribe_link: z.boolean().default(false),
    force_gsm7: z.boolean().default(true),
    // Auto-shorten URLs: upstream short=true replaces any URL in the body
    // with a ros.ms link. Boolean only (no URL from client -> no SSRF).
    shorten_urls: z.boolean().default(false),
    /**
     * Throughput rate to upstream sendSMS API, messages per second.
     * Range 1..200 (upstream infra limit per user/API key).
     * Default 10 = safe baseline for marketing campaigns.
     * Backend dispatcher reads this from campaigns table at pickup time
     * (NEVER from client payload echoed back -- defense V213).
     */
    throttle_msg_per_sec: z
      .number()
      .int()
      .min(1, "throttle_too_low")
      .max(200, "throttle_too_high")
      .default(10),
  })
  .strict();
export type CampaignToggles = z.infer<typeof campaignTogglesSchema>;

/**
 * Per-recipient input. Variables map is optional; missing keys are
 * substituted with the empty string at dispatch time per V135.
 *
 * Variable names: 1..32 chars matching /^[a-z][a-z0-9_]*$/ — same
 * pattern as VARIABLE_NAME_PATTERN in lib/campaigns.ts (V133).
 * Values: plain string, max 256 chars (a single variable should not
 * dominate an SMS body; if they do, restructure the template).
 *
 * Cap: max 32 distinct variable keys per recipient. More than that
 * is almost always a data-modeling error — a recipient with 50
 * variables should be sending a different message, not a templated
 * one.
 */
const variableNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,31}$/, "variable_name_invalid");

const variableValueSchema = z
  .string()
  .max(256, "variable_value_too_long");

// =================================================================
// 1b. WhatsApp campaign subschemas (C1.2a)
// =================================================================

/**
 * WhatsApp variable source for a positional template placeholder {{n}}.
 * Either a CONTACT FIELD (resolved per recipient at enumerate time) or a
 * LITERAL constant (same for the whole campaign — e.g. a promo code).
 *
 * V407 anti-injection: a field key is the SAME strict allowlist used for SMS
 * variables (variableNameSchema, /^[a-z][a-z0-9_]{0,31}$/), so the map can
 * only point at well-formed contact-field keys, never arbitrary expressions.
 */
const waVariableSourceSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("field"),
      key: variableNameSchema,
      // WA-PREFLIGHT (24-Sep): used when the contact's field is empty (so the row never goes out with a blank value).
      fallback: z.string().min(1, "wa_fallback_empty").max(1024, "wa_fallback_too_long").optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("literal"),
      // A single WA body parameter; Meta caps at 1024 chars for body text.
      value: z.string().min(1, "wa_literal_empty").max(1024, "wa_literal_too_long"),
    })
    .strict(),
]);

/**
 * Map of positional template variables -> source.
 * Keys are the positional indices as strings: "1".."20" (Meta body params
 * are positional; our single-send wa_template_params is a positional array).
 * Cap 20 to match the single-send template_params .max(20).
 */
export const campaignWaVariableMapSchema = z
  .record(
    // TL-3b (23-Sep): + "h1" = header text variable, "b<pos>" = dynamic URL button at that 0-based position.
    z.string().regex(/^([1-9]|1[0-9]|20|h1|hm|b[0-9]|c[0-9]_m|c[0-9]_b[01])$/, "wa_var_index_invalid"), // TL-3c/3e: hm media header; c<i>_m / c<i>_b<pos> carousel cards
    waVariableSourceSchema,
  )
  .refine((m) => Object.keys(m).length <= 54, { message: "too_many_wa_variables" });
export type CampaignWaVariableMap = z.infer<typeof campaignWaVariableMapSchema>;

/**
 * Cross-channel SMS fallback (owner rule): a WhatsApp template cannot be
 * auto-converted to SMS (160-char limit + concatenation cost + no media), so
 * the user writes a SEPARATE, channel-correct short SMS. Validated as SMS body
 * length here; R10 additionally enforces presence when enabled. Media/variable
 * semantics of the WA template do NOT carry over (V408).
 */
/**
 * CAMP-VIBER-1 (3-Nov-2026): optional image + button for Viber campaigns on the provider route -- byte-for-byte the
 * single-send `viber` field (lib/send-flow.ts): image = DIRECT https URL ending in .png/.jpg hosted by the customer
 * (the provider validates the extension; short links are refused), button = text (<= 30) + https URL. The server
 * additionally refuses private/IP/localhost hosts and the field itself outside that route.
 */
export const campaignViberRichSchema = z
  .object({
    image_url: z.string().trim().max(2048).regex(/^https:\/\/[^\s/]+\/[^\s]*\.(png|jpe?g)(\?[^\s]*)?$/i, "viber_image_url_invalid").optional(),
    button: z
      .object({ text: z.string().trim().min(1).max(30), url: z.string().trim().max(2048).regex(/^https:\/\/[^\s/]+(\/[^\s]*)?$/i, "viber_button_url_invalid") })
      .strict()
      .optional(),
  })
  .strict();
export type CampaignViberRich = z.infer<typeof campaignViberRichSchema>;

export const campaignFallbackSmsSchema = z
  .object({
    enabled: z.boolean().default(false),
    body: z.string().min(1, "fallback_body_empty").max(1530, "fallback_body_too_long").optional(),
    sender_id: campaignSenderIdSchema.optional(),
  })
  .strict();
export type CampaignFallbackSms = z.infer<typeof campaignFallbackSmsSchema>;


// =================================================================
// 2. Composite request shapes
// =================================================================

/**
 * POST /api/campaigns — create a new campaign in `draft` status.
 *
 * `recipients` is bounded at 100,000 to match the schema 0025 hard
 * cap (campaign_recipients table) and the cron orchestrator's
 * fairness model (5 campaigns / tick * ~20k recipients each ≈ 100k
 * messages per tick across the user base, well within Cloudflare
 * Worker CPU limits).
 *
 * `scheduled_at` is optional — when omitted the campaign starts on
 * the next /start call. When present, R6 enforces it must be at
 * least 60s in the future (anti time-travel + processing buffer).
 *
 * The 7 cross-field refines (R1-R7) live in the superRefine block
 * below. Each is documented at the issue path it raises so error
 * messages map cleanly to UI form fields.
 */
export const campaignCreateInputSchema = z
  .object({
    name: campaignNameSchema,
    channel: campaignChannelSchema,
    sender_id: campaignSenderIdSchema,
    // body_template required for sms/viber, ignored for whatsapp (R9 enforces).
    body_template: campaignBodyTemplateSchema.optional(),
    // C1.2 WhatsApp campaign fields (R9 XOR by channel).
    wa_template_id: uuidSchema.optional(),
    wa_variable_map: campaignWaVariableMapSchema.optional(),
    // C1.2c WhatsApp sender number (Graph phone_number_id of the user's
    // CONNECTED number). Mirrors WaSendMessageInput.phone_number_id in
    // whatsapp-dispatch.ts byte-perfect (digits, 5..32). R11 makes it
    // required for whatsapp + forbidden otherwise. Server re-validates
    // ownership + connected status (V409 anti-IDOR) at create.
    wa_phone_number_id: z
      .string()
      .min(5, "wa_phone_number_id_too_short")
      .max(32, "wa_phone_number_id_too_long")
      .regex(/^\d+$/, "wa_phone_number_id_invalid")
      .optional(),
    fallback_sms: campaignFallbackSmsSchema.optional(),
    // CAMP-VIBER-1: image + button, Viber campaigns on the provider route only (server-checked).
    viber: campaignViberRichSchema.optional(),
    toggles: campaignTogglesSchema.default({
      dedupe_recipients: false,
      apply_user_blocklist: true,
      add_unsubscribe_link: false,
      force_gsm7: true,
      shorten_urls: false,
      throttle_msg_per_sec: 10,
    }),
    scheduled_at: z
      .string()
      .datetime({ offset: true, message: "scheduled_at_invalid" })
      .optional(),
    /**
     * FAZA 1.6 — per-recipient scheduling source key on contacts.custom_json.
     * Mutually exclusive with scheduled_at (enforced by R8 below).
     *
     * Format: snake_case identifier ([a-z][a-z0-9_]{0,31}) matching the key
     * column in contact_field_definitions (must be type='date' server-side,
     * per production schema enum text|number|date in admin-contact-fields.ts).
     *
     * When set, send-to-group resolves contact.custom_json[key] per recipient,
     * validates each value against [now+60s, now+12mo] bounds, and INSERTs
     * into scheduled_messages. Invalid/missing values per-recipient are
     * SKIPPED with audit log (V193), not hard-failed campaign-wide.
     *
     * Server-side strictness:
     * - V191: WHERE user_id=auth.userId on contact_field_definitions (404 on miss)
     * - V192: per-row bounds re-validation (cannot bypass via custom_json injection)
     * - V193: audit log entry on each skip (campaign.recipient_skipped_invalid_scheduled_at)
     */
    scheduled_at_variable: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,31}$/, "invalid_variable_name")
      .optional(),
    template_id: uuidSchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    // R1 — force_gsm7 + body_template must contain only GSM-7 chars.
    // The actual GSM-7 set lives in lib/campaigns.ts (server-side); here
    // we only do a quick approximate check (ASCII printable + a small
    // Latin-1 subset) so the UI can flag obvious cases before submit.
    // Server re-runs the strict check (V134) and may still reject.
    if (val.toggles.force_gsm7 && val.body_template) {
      // Heuristic: any code point >= 0x100 is definitely not GSM-7 basic.
      // We won't catch e.g. "→" (U+2192) here — server check will.
      const looksUcs2 = /[\u0100-\uFFFF]/.test(val.body_template);
      if (looksUcs2) {
        ctx.addIssue({
          code: "custom",
          path: ["body_template"],
          message: "force_gsm7_violated",
        });
      }
    }

    // R2 — add_unsubscribe_link requires literal {gdpr} placeholder.
    // sendSMS message_send_gdpr replaces {gdpr} with the unsubscribe
    // link; without the placeholder the link is appended at the end,
    // which produces ugly truncated messages near the GSM-7 limit.
    if (
      val.toggles.add_unsubscribe_link &&
      val.body_template &&
      !val.body_template.includes("{gdpr}")
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["body_template"],
        message: "missing_gdpr_placeholder",
      });
    }

    // R6 — scheduled_at must be within [now + 60s, now + 12 months] if present.
    // 60s is the cron tick floor + a small buffer; anything tighter
    // risks the campaign starting before the recipients are committed
    // to D1 (eventual-consistency surfaces).
    // 12 months upper bound prevents D1 storage clutter from
    // indefinitely-scheduled campaigns, and aligns with industry
    // practice (Sinch caps at 7 days; we are more generous for
    // marketing planning use cases).
    if (val.scheduled_at !== undefined) {
      const target = Date.parse(val.scheduled_at);
      const earliest = Date.now() + 60_000;
      const latest = Date.now() + 365 * 86_400_000; // 12 months
      if (Number.isNaN(target) || target < earliest) {
        ctx.addIssue({
          code: "custom",
          path: ["scheduled_at"],
          message: "scheduled_at_too_soon",
        });
      } else if (target > latest) {
        ctx.addIssue({
          code: "custom",
          path: ["scheduled_at"],
          message: "scheduled_at_too_far",
        });
      }
    }

    // R7 — Viber + add_unsubscribe_link is unsupported by sendSMS API.
    // The upstream has no `message_send_gdpr` equivalent for ctype=3
    // (Viber); attempting it would produce silent fallback to SMS
    // which the user did not opt into.
    if (val.channel === "viber" && val.toggles.add_unsubscribe_link) {
      ctx.addIssue({
        code: "custom",
        path: ["toggles", "add_unsubscribe_link"],
        message: "unsubscribe_unavailable_on_viber",
      });
    }

    // R8 — scheduled_at and scheduled_at_variable are mutually exclusive.
    // Campaign-level fixed time (scheduled_at) vs per-recipient variable
    // (scheduled_at_variable) cannot coexist — semantically contradictory.
    // Both undefined = immediate send on /start; exactly one set = either
    // fixed campaign start time OR per-recipient resolution at start.
    // FAZA 1.6, threat V191-V193 (per-recipient variable contract).
    if (
      val.scheduled_at !== undefined &&
      val.scheduled_at_variable !== undefined
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["scheduled_at_variable"],
        message: "scheduled_at_and_variable_mutually_exclusive",
      });
    }

    // R9 — channel/content XOR (C1.2). SMS & Viber need a free-text body and
    // must NOT carry WhatsApp template fields. WhatsApp needs an approved
    // template id (server verifies APPROVED + ownership) and ignores body_template.
    if (val.channel === "whatsapp") {
      if (!val.wa_template_id) {
        ctx.addIssue({
          code: "custom",
          path: ["wa_template_id"],
          message: "wa_template_required",
        });
      }
    } else {
      // sms | viber
      if (!val.body_template || val.body_template.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["body_template"],
          message: "body_empty",
        });
      }
      if (val.wa_template_id || val.wa_variable_map) {
        ctx.addIssue({
          code: "custom",
          path: ["wa_template_id"],
          message: "wa_fields_not_allowed_on_channel",
        });
      }
    }

    // R10 — fallback SMS (cross-channel). Only meaningful for digital channels
    // falling back to SMS; when enabled it MUST carry a non-empty body (the
    // separate channel-correct text). Disabled => body/sender ignored.
    if (val.fallback_sms && val.fallback_sms.enabled) {
      if (val.channel === "sms") {
        ctx.addIssue({
          code: "custom",
          path: ["fallback_sms", "enabled"],
          message: "fallback_sms_not_applicable_on_sms",
        });
      }
      if (!val.fallback_sms.body || val.fallback_sms.body.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["fallback_sms", "body"],
          message: "fallback_body_required_when_enabled",
        });
      }
    }

    // R11 — WhatsApp sender number XOR (C1.2c). A WA campaign MUST name the
    // connected number it sends from (wa_phone_number_id); sms/viber must NOT
    // carry it. The number is the Graph routing key (POST /{phone_number_id}/
    // messages) — the server additionally verifies it is owned + connected
    // (V409) before persisting; this guard only shapes the request.
    if (val.channel === "whatsapp") {
      if (!val.wa_phone_number_id) {
        ctx.addIssue({
          code: "custom",
          path: ["wa_phone_number_id"],
          message: "wa_phone_number_id_required",
        });
      }
    } else if (val.wa_phone_number_id) {
      ctx.addIssue({
        code: "custom",
        path: ["wa_phone_number_id"],
        message: "wa_phone_number_id_not_allowed_on_channel",
      });
    }
  });
export type CampaignCreateInput = z.infer<typeof campaignCreateInputSchema>;

/**
 * PATCH /api/campaigns/:id — update a draft campaign.
 *
 * Mutable post-create: `name`, `scheduled_at`, `scheduled_at_variable`.
 * Immutable: channel, sender_id, body_template, toggles, recipients.
 * To change those, the user creates a new campaign (cheap — it's
 * just D1 inserts; the cost only kicks in at /start time).
 *
 * Mutual exclusivity (FAZA 1.6 R8): `scheduled_at` and
 * `scheduled_at_variable` cannot both be set in the resulting campaign
 * state. Partial superRefine catches simultaneous set in one payload;
 * the route handler additionally cross-checks against the current
 * campaign row (e.g. setting variable when scheduled_at is already
 * non-null requires clearing scheduled_at in the same update).
 *
 * Server-side guard (route handler): rejects if campaign.status is
 * not "draft". Cannot mutate a queued/dispatching/completed run.
 */
export const campaignUpdateInputSchema = z
  .object({
    name: campaignNameSchema.optional(),
    scheduled_at: z
      .string()
      .datetime({ offset: true, message: "scheduled_at_invalid" })
      .nullable()
      .optional(),
    /**
     * FAZA 1.6 — toggleable per-recipient scheduling key.
     * `null` = clear existing setting; `undefined` = no change;
     * string = set/replace. Server enforces mutual exclusivity vs
     * scheduled_at against current DB state (R8 + handler guard).
     */
    scheduled_at_variable: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,31}$/, "invalid_variable_name")
      .nullable()
      .optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.name !== undefined ||
      v.scheduled_at !== undefined ||
      v.scheduled_at_variable !== undefined,
    {
      message: "no_fields_to_update",
    },
  )
  .superRefine((val, ctx) => {
    // R6 carry-over — same bounds as create. Skip if scheduled_at is null
    // (null means "remove schedule") or undefined (means "do not change").
    if (typeof val.scheduled_at === "string") {
      const target = Date.parse(val.scheduled_at);
      const earliest = Date.now() + 60_000;
      const latest = Date.now() + 365 * 86_400_000; // 12 months
      if (Number.isNaN(target) || target < earliest) {
        ctx.addIssue({
          code: "custom",
          path: ["scheduled_at"],
          message: "scheduled_at_too_soon",
        });
      } else if (target > latest) {
        ctx.addIssue({
          code: "custom",
          path: ["scheduled_at"],
          message: "scheduled_at_too_far",
        });
      }
    }

    // R8 partial — mutual exclusivity when BOTH explicitly set in this update.
    // Cross-state vs current DB row enforced server-side (route handler reads
    // the campaign row and verifies the resulting state has at most one set).
    // FAZA 1.6 V191-V193.
    if (
      typeof val.scheduled_at === "string" &&
      typeof val.scheduled_at_variable === "string"
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["scheduled_at_variable"],
        message: "scheduled_at_and_variable_mutually_exclusive",
      });
    }
  });
export type CampaignUpdateInput = z.infer<typeof campaignUpdateInputSchema>;

/**
 * POST /api/campaigns/:id/{start|pause|resume|cancel} — lifecycle ops.
 *
 * `reason` is optional free-text logged into the audit chain. Capped
 * at 500 chars to keep the audit payload bounded.
 */
export const campaignActionInputSchema = z
  .object({
    action: z.enum(["start", "pause", "resume", "cancel"]),
    reason: z.string().trim().max(500, "reason_too_long").optional(),
  })
  .strict();
export type CampaignActionInput = z.infer<typeof campaignActionInputSchema>;

/**
 * POST /api/campaigns/preview — dry-run a single body substitution
 * + cost estimate, no DB write, no upstream call.
 *
 * Used by the wizard to live-render the message and live-update the
 * cost estimate as the user types or changes recipients. Cheap on
 * server CPU (pure local compute) and unbilled.
 */
export const campaignPreviewInputSchema = z
  .object({
    channel: campaignChannelSchema,
    sender_id: campaignSenderIdSchema,
    body_template: campaignBodyTemplateSchema,
    toggles: campaignTogglesSchema,
    sample_variables: z
      .record(variableNameSchema, variableValueSchema)
      .refine((v) => Object.keys(v).length <= 32, {
        message: "too_many_variables",
      })
      .optional(),
    sample_phone: phoneSchema.optional(),
  })
  .strict();
export type CampaignPreviewInput = z.infer<typeof campaignPreviewInputSchema>;

/**
 * Schema for POST /api/campaigns/:id/preview (FAZA 1, Step 1.2, V187).
 *
 * Stateful variant of preview — operates on an EXISTING campaign loaded
 * from D1. Server is the single source of truth for channel + sender_id
 * + body_template + toggles (V187 — client cannot inject false body).
 *
 * Inputs are limited to:
 *   - sample_variables: values to substitute into the stored body_template
 *   - sample_phone: REQUIRED for cost computation (runPreview needs a
 *     recipient to detect country + lookup user_pricing rate)
 *
 * Allowed on any campaign state (read-only, no D1 mutation, no audit log).
 */
export const campaignPreviewByIdInputSchema = z
  .object({
    sample_variables: z
      .record(variableNameSchema, variableValueSchema)
      .refine((v) => Object.keys(v).length <= 32, {
        message: "too_many_variables",
      })
      .optional(),
    sample_phone: flexiblePhoneSchema,
  })
  .strict();
export type CampaignPreviewByIdInput = z.infer<
  typeof campaignPreviewByIdInputSchema
>;

/**
 * Schema for POST /api/campaigns/:id/test-send (FAZA 1, Step 1.3, V188).
 *
 * Stateful variant of test-send — operates on an EXISTING campaign loaded
 * from D1. Server is the single source of truth for channel + sender_id
 * + body_template + toggles (V188 — client cannot inject false body to
 * manipulate cost or bypass toggles validation).
 *
 * Inputs are limited to:
 *   - test_phone: REQUIRED (flexiblePhoneSchema, server-side normalize
 *     via libphonenumber-js with user's phone_default_country)
 *   - sample_variables: values to substitute into the stored body_template
 *
 * Behavior:
 *   - Allowed only on campaign.status in {draft, paused} (V174 spirit).
 *   - Costs 1 REAL SMS at production rate (deducted from user.credits).
 *   - When campaign.add_unsubscribe_link toggle is true, uses upstream
 *     sendsms.messages.sendGdpr (auto-appends unsubscribe link); otherwise
 *     uses standard sendsms.messages.send.
 *   - Persists message row with campaign_id=NULL (keeps campaign analytics
 *     clean) and dispatch_source='test' (separate from manual/scheduled).
 *   - Idempotency-Key REQUIRED (V188 prevents double-charge on retry).
 */
export const campaignTestSendByIdInputSchema = z
  .object({
    test_phone: flexiblePhoneSchema,
    sample_variables: z
      .record(variableNameSchema, variableValueSchema)
      .refine((v) => Object.keys(v).length <= 32, {
        message: "too_many_variables",
      })
      .optional(),
  })
  .strict();
export type CampaignTestSendByIdInput = z.infer<
  typeof campaignTestSendByIdInputSchema
>;

/**
 * POST /api/campaigns/test-send — send a single message to a test
 * number for QA before launching the full campaign.
 *
 * Server treats this as a one-off message_send (NOT execute_multiple)
 * so the user does not pay for queue overhead just to validate copy.
 * Counts toward the user's normal SMS quota.
 */
export const campaignTestSendInputSchema = z
  .object({
    channel: campaignChannelSchema,
    sender_id: campaignSenderIdSchema,
    body_template: campaignBodyTemplateSchema,
    toggles: campaignTogglesSchema,
    test_phone: phoneSchema,
    sample_variables: z
      .record(variableNameSchema, variableValueSchema)
      .refine((v) => Object.keys(v).length <= 32, {
        message: "too_many_variables",
      })
      .optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    // R1 + R2 + R7 apply to test-send too — same body, same toggles,
    // would fail in production anyway. Better to fail at preview.
    if (val.toggles.force_gsm7 && /[\u0100-\uFFFF]/.test(val.body_template)) {
      ctx.addIssue({
        code: "custom",
        path: ["body_template"],
        message: "force_gsm7_violated",
      });
    }
    if (
      val.toggles.add_unsubscribe_link &&
      !val.body_template.includes("{gdpr}")
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["body_template"],
        message: "missing_gdpr_placeholder",
      });
    }
    if (val.channel === "viber" && val.toggles.add_unsubscribe_link) {
      ctx.addIssue({
        code: "custom",
        path: ["toggles", "add_unsubscribe_link"],
        message: "unsubscribe_unavailable_on_viber",
      });
    }
  });
export type CampaignTestSendInput = z.infer<typeof campaignTestSendInputSchema>;

// =================================================================
// 3. Response DTOs
// =================================================================

/**
 * Campaign status enum — mirrors campaigns.status in DB schema 0025.
 *
 * Lifecycle:
 *   draft -> queued -> dispatching -> completed_dispatch -> completed
 *   draft -> cancelled (no recipients touched)
 *   any -> error (terminal failure)
 *   queued/dispatching <-> paused (operator-driven)
 *
 * `completed_dispatch` is the brief window where every recipient has
 * been handed to upstream but DLRs are still trickling in. Becomes
 * `completed` once the last DLR lands or the timeout (configurable,
 * default 48h) elapses.
 */
export const campaignStatusSchema = z.enum([
  "draft",
  "enumerating",
  "queued",
  "dispatching",
  "paused",
  "completed_dispatch",
  "completed",
  "cancelled",
  "error",
  "archived",
]);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

/**
 * Campaign DTO — shape returned by GET /api/campaigns and
 * GET /api/campaigns/:id (without the recipients array).
 *
 * Counters reflect the live state on D1 — the cron orchestrator and
 * DLR webhook update them atomically, so the dashboard refreshes
 * naturally without a separate aggregation step.
 *
 * Costs in micro-EUR (1 EUR = 1,000,000 micro) so we can store/sum
 * exactly without floating-point rounding. Frontend divides by 1e6
 * for display.
 */
export const campaignDtoSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  org_id: uuidSchema.nullable(),
  name: campaignNameSchema,
  channel: campaignChannelSchema,
  sender_id: campaignSenderIdSchema,
  // Optional: null/absent for whatsapp campaigns (they use wa_template_id).
  body_template: campaignBodyTemplateSchema.nullable(),
  variables_used: z.array(variableNameSchema),
  template_id: uuidSchema.nullable(),
  // C1.2 WhatsApp campaign fields (null for sms/viber).
  wa_template_id: uuidSchema.nullable(),
  wa_variable_map: campaignWaVariableMapSchema.nullable(),
  // C1.2c — connected sender number persisted on the campaign (null for
  // sms/viber). Always emitted (#217 defense-in-depth: every DTO branch).
  wa_phone_number_id: z.string().nullable(),
  wa_category: z.string().nullable(),
  fallback_sms_enabled: z.boolean(),
  fallback_sms_body: z.string().nullable(),
  fallback_sms_sender_id: campaignSenderIdSchema.nullable(),
  status: campaignStatusSchema,
  // Counters — non-negative integers, monotonic except on cancel
  total_recipients: z.number().int().nonnegative(),
  blocklisted_count: z.number().int().nonnegative(),
  deduped_count: z.number().int().nonnegative(),
  pending_count: z.number().int().nonnegative(),
  dispatching_count: z.number().int().nonnegative(),
  sent_count: z.number().int().nonnegative(),
  delivered_count: z.number().int().nonnegative(),
  read_count: z.number().int().nonnegative(),
  undelivered_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  /** CAMP-SMS-SPLIT-1 (4-Nov-2026): the SMS second deliveries spawned by this campaign's failover (WhatsApp / Viber on
   *  the provider route) -- they are campaign rows, so "delivered" includes them. Only GET /:id computes it; null when
   *  the channel is SMS or the plan is off; absent on the list. */
  fallback_sms_stats: z.object({ fired: z.number().int().nonnegative(), delivered: z.number().int().nonnegative() }).nullable().optional(),
  // Cost estimates / actuals — micro-EUR, can be 0 for draft
  estimated_cost_micro_eur: z.number().int().nonnegative(),
  actual_cost_micro_eur: z.number().int().nonnegative(),
  // Toggles snapshot at create time — never mutates after
  toggles: campaignTogglesSchema,
  // Timestamps — ISO 8601 strings
  scheduled_at: z.string().datetime({ offset: true }).nullable(),
  /**
   * FAZA 1.6 — per-recipient scheduling source key (snake_case identifier
   * matching a contact_field_definitions.key with type='date'). Mutually
   * exclusive with scheduled_at (server-enforced R8 at create/update).
   * null = not configured (immediate or fixed-time campaign).
   */
  scheduled_at_variable: z.string().nullable(),
  started_at: z.string().datetime({ offset: true }).nullable(),
  paused_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  cancelled_at: z.string().datetime({ offset: true }).nullable(),
  archived_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  error_message: z.string().nullable(),
  version: z.number().int().nonnegative(),
});
export type CampaignDto = z.infer<typeof campaignDtoSchema>;

/**
 * Per-recipient DTO — shape returned by
 * GET /api/campaigns/:id/recipients (paginated).
 *
 * `dlr_status_code` uses the sendSMS Report Mask vocabulary
 * (1=delivered, 2=undelivered, 4=queued, 8=sent, 16=failed) which is
 * what the DLR webhook receives. Different from MessageStatus codes!
 * See routes/webhooks.ts file-level comment for the full disambiguation.
 */
/**
 * Live dispatch monitor DTO — shape returned by
 * GET /api/campaigns/:id/monitor for the wizard's last step
 * (DispatchMonitor component, S4.B.7).
 *
 * Snapshots all per-status aggregates plus a recent-events tail
 * (last 50 dispatched/delivered/failed). Frontend polls this every
 * 2-5s during dispatching, and stops polling once status transitions
 * to `completed` or `cancelled`.
 */
// =================================================================
// 4. List query parameters
// =================================================================

/**
 * Query parameters for GET /api/campaigns.
 *
 * Default sort is created_at DESC (newest first) — matches the
 * conventional dashboard reading direction. `cursor` is opaque from
 * the client's perspective; server uses (created_at, id) tuple to
 * disambiguate same-instant rows.
 */
export const campaignListQuerySchema = z
  .object({
    status: campaignStatusSchema.optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();
export type CampaignListQuery = z.infer<typeof campaignListQuerySchema>;

// NOTE: campaignRecipientsQuerySchema dropped in PIVOT v4. For per-message detail of a campaign, use messages history endpoint with ?campaign_id=<uuid> filter.
